/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import React, {
  ForwardedRef,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import withStyles from "@mui/styles/withStyles";

import {
  DEFAULT_PROJECTION,
  DEFAULT_VIEWPORT,
  MapProjection,
  ViewportType,
  isValidViewport,
  mergeViewports,
  viewportAttributeValues,
  getMapViewport,
} from "./utils";

import ReactMapGL, { AttributionControl, MapRef } from "react-map-gl";
import mapboxgl from "mapbox-gl";
import mapboxWorkerUrl from "mapbox-gl/dist/mapbox-gl-csp-worker.js?url";

import { trackException } from "../../services/Telemetry";
import "mapbox-gl/dist/mapbox-gl.css";

type ReactMapGLProps = React.ComponentProps<typeof ReactMapGL>;

// set the worker separately, to avoid transpilation
// per https://docs.mapbox.com/mapbox-gl-js/guides/install/#loading-and-transpiling-the-web-worker-separately
// `?url` keeps it a classic worker: under `?worker` it becomes a module worker, where its importScripts throws.
// real mapbox-gl property that @types/mapbox-gl leaves undeclared
(mapboxgl as typeof mapboxgl & { workerUrl: string }).workerUrl =
  mapboxWorkerUrl;

const styles = () => ({
  root: {
    position: "relative" as const,
    width: "100%",
    height: "100%",
    zIndex: 0,
  },
});

interface SharedMapComponentProps {
  /** MUI classes passed from with styles */
  classes: any;

  /** A function that returns the map instance */
  children?: (c: any) => {};

  /** Custom css class for styling */
  customClass?: string;

  /** Custom HTML to put in the AttributionControl */
  customAttribution?: string;

  /** An object that defines the viewport
   * @see https://uber.github.io/react-map-gl/#/Documentation/api-reference/interactive-map?section=initialization
   */
  viewport?: ViewportType;

  /** A boolean that allows panning */
  dragPan?: boolean;

  /** A boolean that allows rotating */
  dragRotate?: boolean;

  /** A boolean that allows zooming */
  scrollZoom?: boolean;

  /** A boolean that allows zooming */
  touchZoom?: boolean;

  /** A boolean that allows touch rotating */
  touchRotate?: boolean;

  /** A boolean that allows double click zooming */
  doubleClickZoom?: boolean;

  mapboxAccessToken: string;

  /** A style URL or document. react-map-gl's bundled Style type differs
   * from @types/mapbox-gl's; both describe the same object at runtime. */
  mapStyle: ReactMapGLProps["mapStyle"] | mapboxgl.Style;

  cursor?: ReactMapGLProps["cursor"];
  onIdle?: ReactMapGLProps["onIdle"];
  onData?: ReactMapGLProps["onData"];
  interactiveLayerIds?: [];

  /** Mapbox projection to render in. Defaults to Mercator when omitted.
   * @see https://docs.mapbox.com/mapbox-gl-js/guides/projections/
   */
  projection?: MapProjection;

  /** Camera padding, applied from the map's first frame so a rebuilt map does
   * not flash unpadded before its load event. */
  padding?: { top: number; right: number; bottom: number; left: number };

  /** Callback functions */
  onClick?: (e) => void;
  onLoad?: (e) => void;

  /** A callback run before the Map makes a request for an external URL.
   * The callback can be used to modify the url, set headers, or set the credentials property for cross-origin requests.
   * Expected to return a RequestParameters object with a url property and optionally headers and credentials properties. */
  transformRequest?: ReactMapGLProps["transformRequest"];

  /** A function that exposes the viewport */
  onViewportChange?: (viewport: any) => void;

  /** Mapbox `error` event handler. Runs after the failure is reported. */
  onError?: (event: any) => void;
}

const SharedMapComponent = forwardRef(
  (props: SharedMapComponentProps, mapRef: ForwardedRef<MapRef>) => {
    const latestViewportRequest = useRef(null);
    const {
      classes,
      customClass,
      customAttribution,
      children,
      // getCursor,
      dragPan = true,
      dragRotate = true,
      scrollZoom = true,
      touchZoom = true,
      touchRotate = true,
      doubleClickZoom = true,
      mapStyle,
      projection = DEFAULT_PROJECTION,
      ...mapboxProps
    } = props;

    const [viewport, setViewport] = useState({ ...DEFAULT_VIEWPORT });
    const [isLoaded, setIsLoaded] = useState(false);
    const [isMoving, setIsMoving] = useState(false);
    const [mapContainer, setMapContainer] = useState<HTMLDivElement | null>(
      null
    );

    const updateViewport = (newViewportModel) => {
      const newViewport = mergeViewports(newViewportModel, viewport);

      setViewport(newViewport);

      return newViewport;
    };

    // callers pass an object ref; a callback ref has no map to read
    const mapRefObject = typeof mapRef === "function" ? null : mapRef;
    const map = useMemo(
      () => mapRefObject?.current?.getMap(),
      [mapRefObject?.current]
    );

    useEffect(() => {
      if (map && isLoaded) {
        const { latitude, longitude, zoom, pitch, bearing } = viewport;

        if (!isValidViewport(viewport)) {
          trackException(
            new Error("Attempted to go to invalid location"),
            { source: "map", viewport: JSON.stringify(viewport) },
            { dedupeKey: "map:invalidViewport" }
          );
          console.error("Attempted to go to invalid location", viewport);

          return;
        }

        setIsMoving(true);

        map.flyTo({
          center: [longitude, latitude],
          zoom: zoom,
          pitch: pitch,
          bearing: bearing,
        });

        mapboxProps?.onViewportChange?.(viewport);
      }
    }, [map, ...viewportAttributeValues(viewport)]);

    // When viewport props change, merge changes into the local viewport state
    useEffect(() => {
      if (!isLoaded) {
        return;
      }

      latestViewportRequest.current = mapboxProps.viewport;

      // Use setTimeout to defer processing to the next event loop tick,
      // ensuring that rapid sequential viewport updates don't create race conditions.
      // Only the last viewport update in a sequence will be processed, preventing
      // competing updates from interfering with each other.
      const timeoutId = setTimeout(() => {
        if (latestViewportRequest.current === mapboxProps.viewport) {
          const newViewport = updateViewport(mapboxProps.viewport);

          if (viewport !== newViewport) {
            mapboxProps?.onViewportChange?.(newViewport);
          }
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }, [isLoaded, ...viewportAttributeValues(mapboxProps.viewport)]);

    const onLoad = (e) => {
      mapboxProps?.onLoad?.(e);
      setIsLoaded(true);
    };

    // Mapbox reports style, source and tile failures as `error` events rather
    // than throwing, so without this a broken layer only ever reaches the
    // console. Deduped per source: a failing tileset fires on every tile.
    const onError = (e) => {
      const error = e?.error ?? e;

      trackException(
        error,
        {
          source: "mapbox",
          sourceId: e?.sourceId,
          // AJAXError carries the HTTP status; the url is deliberately not
          // reported — user-configured tile endpoints can hold credentials
          ...(error?.status && { status: String(error.status) }),
        },
        {
          dedupeKey: `mapbox:${e?.sourceId ?? "map"}:${error?.message}`,
        }
      );

      // react-map-gl only logs error events when no onError prop is supplied,
      // so keep them visible in the console now that we supply one
      console.error(error);
      mapboxProps?.onError?.(e);
    };

    const onMoveEnd = ({ target, viewState = null }) => {
      // Skip updates if controlled movement is happening of if map has not loaded
      if (!isLoaded || isMoving) {
        setIsMoving(false);

        return;
      }

      const newViewportModel = viewState ?? getMapViewport(target);
      const newViewport = updateViewport(newViewportModel);

      if (mapboxProps.viewport !== newViewport) {
        mapboxProps?.onViewportChange?.(newViewport);
      }
    };

    return (
      <div
        ref={(element) => setMapContainer(element)}
        className="marapp-qa-map"
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          zIndex: 0,
        }}
      >
        {mapContainer && (
          <ReactMapGL
            ref={mapRef}
            // CUSTOM PROPS FROM REACT MAPBOX API
            {...mapboxProps}
            mapStyle={mapStyle as ReactMapGLProps["mapStyle"]}
            maxZoom={viewport.maxZoom}
            minZoom={viewport.minZoom}
            // Fit to container
            style={{
              width: "100%",
              height: "100%",
            }}
            projection={{ name: projection }}
            // INTERACTIVITY
            dragPan={dragPan}
            dragRotate={dragRotate}
            scrollZoom={scrollZoom}
            doubleClickZoom={doubleClickZoom}
            // Whether to display the default attribution control
            attributionControl={false}
            // Event Listeners
            onLoad={onLoad}
            onMoveEnd={onMoveEnd}
            onError={onError}
          >
            {isLoaded &&
              !!mapRef &&
              (typeof children === "function" ? children(map) : children)}
            <div className="mapboxgl-ctrl-parent">
              <AttributionControl
                compact={true}
                customAttribution={customAttribution}
              />
            </div>
          </ReactMapGL>
        )}
      </div>
    );
  }
);

export const Map = withStyles(styles)(SharedMapComponent);

export { DEFAULT_VIEWPORT };
